import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKlantenStore, type BrandFlags } from '../stores/klanten-store';
import { CallButton } from '../components/phone/CallButton';
import { RecentCustomers } from '../components/klanten/RecentCustomers';
import { NieuweKlantModal } from '../components/klanten/NieuweKlantModal';
import { formatCurrency, formatDate } from '../lib/format';
import { BRAND_COLORS } from '../lib/order-constants';
import clsx from 'clsx';

function BrandChips({ flags }: { flags?: BrandFlags }) {
  if (!flags) return null;
  const fallback = 'bg-slate-100 text-slate-600';
  const active: Array<[string, string]> = [];
  if (flags.kleda) active.push(['KLEDA', BRAND_COLORS.KLEDA ?? fallback]);
  if (flags.cinco) active.push(['CINCO', BRAND_COLORS.CINCO ?? fallback]);
  if (flags.sofasaver) active.push(['SOFASAVER', BRAND_COLORS.SOFASAVER ?? fallback]);
  if (flags.schoonhuisshop) active.push(['SHS', BRAND_COLORS.SCHOONHUISSHOP ?? fallback]);
  if (active.length === 0) return null;
  return (
    <>
      {active.map(([label, cls]) => (
        <span key={label} className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', cls)}>
          {label}
        </span>
      ))}
    </>
  );
}

function ResultSkeleton() {
  return (
    <div className="px-6 py-4 flex items-center gap-4 animate-pulse">
      <div className="w-10 h-10 bg-slate-200 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-slate-200 rounded w-40" />
        <div className="h-3 bg-slate-100 rounded w-56" />
      </div>
    </div>
  );
}

export function KlantenPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchInput, setSearchInput] = useState('');
  const [showNieuweKlant, setShowNieuweKlant] = useState(false);

  const {
    searchResults,
    isSearching,
    searchCustomers,
    setSearchQuery,
  } = useKlantenStore();

  // Auto-focus search on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults]);

  // Scroll selected into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-result-item]');
      items[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleSearch = useCallback((value: string) => {
    setSearchInput(value);
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchCustomers(value);
    }, 300);
  }, [searchCustomers, setSearchQuery]);

  const openResult = useCallback((result: typeof searchResults[0]) => {
    navigate(`/klanten/${result.id}?type=${result.type}`);
  }, [navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < searchResults.length) {
      e.preventDefault();
      openResult(searchResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      setSearchInput('');
      setSearchQuery('');
      inputRef.current?.blur();
    }
  }, [searchResults, selectedIndex, openResult, setSearchQuery]);

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-slate-900">Klanten</h1>
            <button
              type="button"
              onClick={() => setShowNieuweKlant(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
            >
              + Nieuwe klant
            </button>
          </div>

          {/* Search bar */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              placeholder="Zoek op naam, email, telefoon, postcode of WO-nummer..."
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-10 py-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            {searchInput && (
              <button
                onClick={() => handleSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {isSearching && (
              <div className="absolute right-10 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Keyboard hints */}
          {searchResults.length > 0 && (
            <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-400">
              <span><kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">{'\u2191\u2193'}</kbd> navigeren</span>
              <span><kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">Enter</kbd> openen</span>
              <span><kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">Esc</kbd> sluiten</span>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto" ref={listRef}>
          {isSearching && searchResults.length === 0 ? (
            <div className="divide-y divide-slate-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <ResultSkeleton key={i} />
              ))}
            </div>
          ) : searchResults.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {searchResults.map((result, idx) => (
                <div
                  key={result.id}
                  data-result-item
                  role="button"
                  tabIndex={0}
                  onClick={() => openResult(result)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openResult(result);
                    }
                  }}
                  className={clsx(
                    'w-full px-6 py-4 flex items-center gap-4 transition-colors text-left cursor-pointer',
                    selectedIndex === idx
                      ? 'bg-indigo-50'
                      : 'hover:bg-slate-50'
                  )}
                >
                  {/* Avatar */}
                  <div className={clsx(
                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                    result.type === 'account'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  )}>
                    <span className="text-sm font-semibold">
                      {result.name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-sm font-medium text-slate-900 truncate">{result.name}</span>
                      <span className={clsx(
                        'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide flex-shrink-0',
                        result.type === 'lead'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      )}>
                        {result.type === 'lead' ? 'Lead' : 'Klant'}
                      </span>
                      <BrandChips flags={result.brandFlags} />
                      {result.openInvoiceAmount !== undefined && result.openInvoiceAmount > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/klanten/${result.id}?type=${result.type}&tab=facturen`);
                          }}
                          title="Open factuur-overzicht"
                          className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 flex-shrink-0"
                        >
                          {formatCurrency(result.openInvoiceAmount)} open
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {result.email && <span className="truncate">{result.email}</span>}
                      {result.phone && <span>{result.phone}</span>}
                      {result.city && <span>{result.city}</span>}
                      {result.postcode && <span>{result.postcode}</span>}
                    </div>
                    {result.orderCount !== undefined && result.orderCount > 0 && (
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                        <span>{result.orderCount} {result.orderCount === 1 ? 'order' : 'orders'}</span>
                        <span>·</span>
                        <span>{formatCurrency(result.totalSpend ?? 0)}</span>
                        {result.lastOrderDate && (
                          <>
                            <span>·</span>
                            <span>laatste {formatDate(result.lastOrderDate)}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Call button */}
                  {result.phone && (
                    <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                      <CallButton
                        number={result.phone}
                        customerId={result.id}
                        customerName={result.name}
                        sourceContext="klanten-lijst"
                        variant="icon"
                      />
                    </div>
                  )}

                  {/* Arrow */}
                  <svg className="w-4 h-4 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              ))}
            </div>
          ) : searchInput && !isSearching ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.182 16.318A4.486 4.486 0 0012.016 15a4.486 4.486 0 00-3.198 1.318M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
              </svg>
              <p className="text-sm">Geen klanten gevonden voor "{searchInput}"</p>
            </div>
          ) : (
            <RecentCustomers />
          )}
        </div>
      </div>
      <NieuweKlantModal isOpen={showNieuweKlant} onClose={() => setShowNieuweKlant(false)} />
    </>
  );
}
